import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { FaCheck, FaTimes, FaEye } from 'react-icons/fa';
import '../styles/AssetUserApprovalPage.css';

const AssetUserApprovalPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const token = sessionStorage.getItem('token');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `http://localhost:4000/api/labAdmin/asset-user-requests?status=${filter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests(res.data.data || []);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [filter, token]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleApprove = async (requestId) => {
    try {
      setActionLoading(true);
      await axios.patch(
        `http://localhost:4000/api/labAdmin/asset-user-requests/${requestId}/approve`,
        { notes: '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedRequest(null);
      setShowModal(false);
      fetchRequests();
    } catch (err) {
      console.error(err);
      setError('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (requestId) => {
    if (!rejectionReason.trim()) {
      setError('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      await axios.patch(
        `http://localhost:4000/api/labAdmin/asset-user-requests/${requestId}/reject`,
        { rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedRequest(null);
      setShowModal(false);
      setRejectionReason('');
      fetchRequests();
    } catch (err) {
      console.error(err);
      setError('Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const openModal = (request) => {
    setSelectedRequest(request);
    setShowModal(true);
    setRejectionReason('');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRequest(null);
    setRejectionReason('');
  };

  if (loading) return <div className="approval-loading">Loading requests...</div>;

  return (
    <div className="asset-user-approval-page">
      <div className="approval-header">
        <h2>Asset User Signup Requests</h2>
      </div>

      <div className="approval-filters">
        <button
          className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
          onClick={() => setFilter('pending')}
        >
          Pending ({requests.filter(r => r.status === 'pending').length})
        </button>
        <button
          className={`filter-btn ${filter === 'approved' ? 'active' : ''}`}
          onClick={() => setFilter('approved')}
        >
          Approved
        </button>
        <button
          className={`filter-btn ${filter === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilter('rejected')}
        >
          Rejected
        </button>
        <button
          className={`filter-btn ${filter === '' ? 'active' : ''}`}
          onClick={() => setFilter('')}
        >
          All
        </button>
      </div>

      {error && <div className="approval-error">{error}</div>}

      {requests.length === 0 ? (
        <div className="approval-empty">
          <p>No requests found</p>
        </div>
      ) : (
        <div className="approval-requests">
          {requests.map(request => (
            <div key={request._id} className={`request-card request-${request.status}`}>
              <div className="request-header">
                <h3>{request.firstName} {request.lastName}</h3>
                <span className={`status-badge ${request.status}`}>{request.status?.toUpperCase()}</span>
              </div>

              <div className="request-details">
                <p><strong>Email:</strong> {request.email}</p>
                <p><strong>Requested:</strong> {new Date(request.requestedAt).toLocaleString()}</p>
                {request.approvedAt && (
                  <p><strong>Approved:</strong> {new Date(request.approvedAt).toLocaleString()}</p>
                )}
                {request.rejectedAt && (
                  <p><strong>Rejected:</strong> {new Date(request.rejectedAt).toLocaleString()}</p>
                )}
              </div>

              {request.status === 'pending' && (
                <div className="request-actions">
                  <button
                    className="btn-view"
                    onClick={() => openModal(request)}
                  >
                    <FaEye /> Review
                  </button>
                </div>
              )}

              {request.status === 'rejected' && request.rejectionReason && (
                <div className="rejection-reason">
                  <strong>Reason:</strong> {request.rejectionReason}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Review Modal */}
      {showModal && selectedRequest && (
        <div className="approval-modal-overlay">
          <div className="approval-modal">
            <div className="modal-header">
              <h3>Review Request</h3>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="modal-content">
              <div className="request-info">
                <h4>Applicant Information</h4>
                <p><strong>Name:</strong> {selectedRequest.firstName} {selectedRequest.lastName}</p>
                <p><strong>Email:</strong> {selectedRequest.email}</p>
                <p><strong>Requested:</strong> {new Date(selectedRequest.requestedAt).toLocaleString()}</p>
              </div>

              {selectedRequest.status === 'pending' && (
                <div className="action-section">
                  <div className="rejection-form">
                    <label><strong>Rejection Reason (if rejecting):</strong></label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Enter reason for rejection..."
                      rows={3}
                    />
                  </div>

                  <div className="modal-actions">
                    <button
                      className="btn-approve"
                      onClick={() => handleApprove(selectedRequest._id)}
                      disabled={actionLoading}
                    >
                      <FaCheck /> Approve
                    </button>
                    <button
                      className="btn-reject"
                      onClick={() => handleReject(selectedRequest._id)}
                      disabled={actionLoading}
                    >
                      <FaTimes /> Reject
                    </button>
                    <button
                      className="btn-cancel"
                      onClick={closeModal}
                      disabled={actionLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetUserApprovalPage;
